/**
 * THE GIFT EBOOK, AS A REAL CART LINE. 2026-09-08.
 *
 * The Sunday Rule is promised on the product page, on the home page and in the
 * FAQ, and until now it was a sentence. A buyer reaching Shopify's payment page
 * saw one pillow and one price, and nothing to say the book was coming — the
 * one screen where a promise has to be visible is the one where the money is
 * committed. The Digital Products app that actually emails the file is attached
 * to the PILLOW and is untouched by any of this: what follows only makes the
 * gift visible in the cart drawer, on /cart, and at checkout.
 *
 * Two halves:
 *
 *   1. THE ADD. Every add-to-cart on this store funnels through
 *      assets/product-form.js — the three custom buy boxes
 *      (snippets/d2-offer-body.liquid, sections/d2-close.liquid,
 *      sections/d2-lst-buy.liquid) all wrap their form in
 *      <product-form-component>, and so do Horizon's own buy-buttons and
 *      quick-add. So product-form.js asks this file, once, whether the request
 *      it is about to send should carry a second item, and this file answers by
 *      handing back a JSON body with both. One request, one cart render, no
 *      second row appearing a beat late.
 *
 *      (The home page colour cards are links to the PDP, not adds — checked
 *      against sections/d2-home-shades.liquid — and the sticky bar is an
 *      anchor to #d2-offer. Neither needs anything here.)
 *
 *   2. THE GUARD. A cart can end up wrong in ways the add path cannot see: a
 *      cart built before this shipped, a pillow removed leaving the gift alone,
 *      a quantity edited from somewhere else. After every cart update this file
 *      re-reads /cart.js and repairs three states — gift without a pillow,
 *      gift missing while a pillow is in the cart, gift at any quantity but one.
 *
 * WHY IT CANNOT LOOP: the repair dispatches its own CartLinesUpdateEvent so the
 * drawer and /cart re-render, which would re-enter the listener. The resolved
 * detail carries `source: 'd2-gift'` and the listener returns on sight of it.
 * That is the whole cycle break — not a timer, not a counter.
 *
 * WHERE THE VARIANT ID COMES FROM: snippets/scripts.liquid resolves the handle
 * `the-sunday-rule-ebook` through `all_products` at render time and writes the
 * live id into `Theme.gift`. Nothing here is hard-coded, so if the product is
 * ever unpublished or deleted `Theme.gift.variantId` is simply absent and both
 * halves switch themselves off — the pillow still adds normally. A written-in
 * id would instead break every add-to-cart on the store the day it went stale.
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Stamped on the repair event so the listener can ignore its own work. */
const SOURCE = 'd2-gift';

/**
 * The gift variant id as a string, or null when the gift is off.
 *
 * `Theme` is read bare and inside a try, deliberately. snippets/scripts.liquid
 * declares it with `const` in a classic script, which puts it in the global
 * LEXICAL scope and not on `globalThis` — `globalThis.Theme` is undefined even
 * on a page where every other module is happily reading `Theme.routes`. The try
 * covers the case where the object is not there at all.
 *
 * @returns {string | null}
 */
export function giftVariantId() {
  let id;
  try {
    id = Theme?.gift?.variantId;
  } catch {
    return null;
  }
  if (id === undefined || id === null || id === '') return null;
  return String(id);
}

/**
 * True when a line of this variant earns the gift. The book is attached to
 * the pillow, so only pillow variants qualify (snippets/scripts.liquid writes
 * their ids). If that list is missing, any paid line qualifies, which is how
 * the store behaved before the blanket — the pillow keeps getting its book.
 *
 * @param {string | number} variantId
 * @returns {boolean}
 */
function qualifies(variantId) {
  const giftId = giftVariantId();
  const id = String(variantId);
  if (!giftId || id === giftId) return false;
  let list;
  try {
    list = Theme?.gift?.qualifyingIds;
  } catch {
    list = null;
  }
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.some((entry) => String(entry) === id);
}

/**
 * Reads the live cart.
 * @returns {Promise<any | null>}
 */
async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) return null;
  return response.json();
}

/**
 * The section ids the cart renders into, so a cart mutation can come back with
 * the HTML to morph. Same set product-form.js and component-cart-items.js
 * collect; recomputed here rather than passed in because the guard runs from a
 * page event with no form in hand.
 * @returns {string}
 */
function cartSectionIds() {
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids.join(',');
}

/**
 * Decides whether an add-to-cart should carry the gift, and if so builds the
 * multi-item JSON body that replaces the form-encoded one.
 *
 * Returns null — meaning "send the request you were going to send" — when the
 * gift is switched off, when the thing being added IS the gift (its own product
 * page is reachable by direct link), or when the cart already holds it. That
 * last check is why this is async: one GET /cart.js before the POST is what
 * stops a second pillow from adding a second book.
 *
 * @param {FormData} formData - The serialised add-to-cart form.
 * @returns {Promise<Record<string, any> | null>}
 */
export async function giftAddPayload(formData) {
  const giftId = giftVariantId();
  if (!giftId) return null;

  const addingId = String(formData.get('id') ?? '');
  if (!addingId || !qualifies(addingId)) return null;

  try {
    const cart = await readCart();
    if (!cart) return null;
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (items.some((item) => String(item.variant_id) === giftId)) return null;
  } catch {
    // Could not read the cart. Send the plain add rather than risk a duplicate
    // line; the guard below repairs the missing gift on the very next update.
    return null;
  }

  /** @type {Record<string, any>} */
  const main = { id: Number(addingId), quantity: Number(formData.get('quantity')) || 1 };

  // A JSON body cannot carry the form's other fields for free, so the two that
  // change what gets bought are copied across by hand. Neither is in use on
  // this store today; they are here so a future selling plan or engraving field
  // does not vanish the moment the gift starts riding along.
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (match && typeof value === 'string') properties[match[1]] = value;
  }
  if (Object.keys(properties).length > 0) main.properties = properties;

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) main.selling_plan = Number(sellingPlan);

  // THE GIFT GOES FIRST IN THE REQUEST SO IT COMES SECOND IN THE CART.
  // /cart/add.js returns a multi-item add in the reverse of the order sent —
  // measured on this store, not assumed: sending [pillow, gift] put the free
  // book on line 1, sending [gift, pillow] put the pillow on line 1. The cart
  // drawer, /cart and Shopify's own checkout summary all read in cart order,
  // and a $0 row above the thing that was actually bought reads like the store
  // lost track of the order.
  /** @type {Record<string, any>} */
  const payload = { items: [{ id: Number(giftId), quantity: 1 }, main] };

  // product-form.js appends `sections` once per cart-items-component and each
  // append carries the list accumulated so far, so the LAST value is the
  // complete one. Taking the first would ask for half the sections and leave
  // the second cart surface stale.
  const sections = formData.getAll('sections');
  const complete = sections[sections.length - 1];
  if (typeof complete === 'string' && complete) {
    payload.sections = complete;
    payload.sections_url = window.location.pathname;
  }

  return payload;
}

/**
 * Sends an add-to-cart, with the gift attached when it should be.
 *
 * `sendPlain` is the request product-form.js had already built. It is called
 * unchanged whenever the gift is not riding along, so the untouched path stays
 * byte-for-byte what Horizon ships.
 *
 * @param {FormData} formData
 * @param {() => Promise<Response>} sendPlain
 * @returns {Promise<Response>}
 */
export async function addToCartWithGift(formData, sendPlain) {
  const payload = await giftAddPayload(formData);
  if (!payload) return sendPlain();

  return fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('json', { body: JSON.stringify(payload) }),
    credentials: 'same-origin',
  });
}

/**
 * Adds the gift to an already-JSON list of items, for the batched add
 * product-form.js uses when a variant change is still in flight.
 *
 * @param {Array<{id: number, quantity: number}>} items
 * @returns {Promise<Array<{id: number, quantity: number}>>}
 */
export async function itemsWithGift(items) {
  const giftId = giftVariantId();
  if (!giftId) return items;
  if (items.some((item) => String(item.id) === giftId)) return items;
  if (!items.some((item) => qualifies(item.id))) return items;

  try {
    const cart = await readCart();
    if (!cart) return items;
    const lines = Array.isArray(cart.items) ? cart.items : [];
    if (lines.some((line) => String(line.variant_id) === giftId)) return items;
  } catch {
    return items;
  }

  // First in the request, last in the cart — same reversal noted above.
  return [{ id: Number(giftId), quantity: 1 }, ...items];
}

// ---- the guard -------------------------------------------------------------

let running = false;

/**
 * Sets the gift line to `quantity` and tells the page about it.
 *
 * /cart/update.js is the one cart endpoint that covers all three repairs:
 * keyed by variant id it sets a quantity, 0 removes the line, and it ADDS the
 * line when that variant is not in the cart yet (Ajax Cart API reference).
 * change.js cannot do the last one and add.js cannot do the first two.
 *
 * The event is dispatched with action 'update' or 'remove', never 'add':
 * cart-drawer.js auto-opens on 'add', and a drawer that flies open because a
 * book was quietly restored would be the store talking to itself.
 *
 * @param {string} giftId
 * @param {number} quantity
 */
async function setGiftQuantity(giftId, quantity) {
  /** @type {Record<string, any>} */
  const body = { updates: { [giftId]: quantity } };
  const sections = cartSectionIds();
  if (sections) {
    body.sections = sections;
    body.sections_url = window.location.pathname;
  }

  // WAIT FOR THE ROOM TO FILL BEFORE SPEAKING. The listeners that redraw the
  // cart — cart-items-component, cart-icon, cart-drawer-component — install
  // themselves in connectedCallback, which runs when their custom element is
  // defined. This file's own module executes earlier in document order than
  // component-cart-items.js does, so a repair fired at page load reached zero
  // listeners: the cart data was corrected and the page went on showing the old
  // rows. Caught on /cart 2026-09-08 — the gift was added, the row never
  // appeared. whenDefined resolves after existing elements have been upgraded
  // and connected. The race means a page with no cart component at all still
  // gets its data repaired; it just has nothing to redraw.
  await Promise.race([
    customElements.whenDefined('cart-items-component'),
    new Promise((resolve) => setTimeout(resolve, 3000)),
  ]);

  const deferred = CartLinesUpdateEvent.createPromise();

  document.body.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity === 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ id: giftId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }));
    const cart = await response.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        cart,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Reads the cart and repairs the gift line if it is wrong. Does nothing — no
 * request at all — when the cart is already right, which is the normal case
 * after an add that already carried the gift.
 */
async function reconcile() {
  const giftId = giftVariantId();
  if (!giftId) {
    // Gift switched off (product unpublished): nothing to repair, but the
    // header count still has to follow the cart.
    try {
      const cart = await readCart();
      if (cart) syncHeaderCount(cart);
    } catch {
      // Leave the header as the page loaded it.
    }
    return;
  }
  if (running) return;

  running = true;
  try {
    const cart = await readCart();
    if (!cart) return;

    syncHeaderCount(cart);

    const items = Array.isArray(cart.items) ? cart.items : [];
    const gift = items.find((item) => String(item.variant_id) === giftId);
    const hasOther = items.some((item) => qualifies(item.variant_id));

    let target = null;
    if (gift && !hasOther) target = 0;
    else if (hasOther && !gift) target = 1;
    else if (hasOther && gift && gift.quantity !== 1) target = 1;

    if (target === null) return;

    await setGiftQuantity(giftId, target);
  } catch (error) {
    console.warn('[d2-gift] could not reconcile the gift line', error);
  } finally {
    running = false;
  }
}

// ---- the header count --------------------------------------------------------

/**
 * Writes the visible item count into the header. The three nodes are the
 * ones sections/duet-header.liquid renders from snippets/d2-cart-count.liquid
 * on the server; nothing else on the page redraws them, because the header
 * is not one of the sections a cart mutation asks Shopify to re-render. So
 * until 2026-09-08 the header said whatever the page had loaded with — Leo
 * saw "CART (7)" beside a drawer that said "8 items" — and now it follows.
 *
 * The count leaves the gift out, the same arithmetic as the Liquid snippet:
 * one pillow reads 1, not the 2 Shopify counts.
 *
 * @param {any} cart - A /cart.js response.
 */
function syncHeaderCount(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const giftId = giftVariantId();
  let count = 0;
  for (const item of items) {
    if (giftId && String(item.variant_id) === giftId) continue;
    count += Number(item.quantity) || 0;
  }
  const text = String(count);
  for (const node of document.querySelectorAll('.duet-header__count')) node.textContent = text;
  for (const node of document.querySelectorAll('.duet-header__label[href*="/cart"]')) {
    node.textContent = `Cart (${text})`;
  }
  for (const node of document.querySelectorAll('.duet-header__icon--cart .visually-hidden')) {
    node.textContent = `Cart, ${text} item${count === 1 ? '' : 's'}`;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const cartEvent = /** @type {CartLinesUpdateEvent} */ (event);

  cartEvent.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      // The repair's own update carries the fresh cart in its detail; every
      // other mutation gets one read, which is the read reconcile() needs anyway.
      if (detail?.source === SOURCE) {
        if (detail?.cart) syncHeaderCount(detail.cart);
        return;
      }
      reconcile();
    })
    .catch(() => {
      // A failed cart mutation is the mutation's problem to report, not ours.
    });
});

/**
 * True when the page was served with line items in it. Read from the markup
 * rather than fetched, because the whole point of asking is to avoid a fetch.
 * @returns {boolean}
 */
function cartLooksNonEmpty() {
  return Boolean(document.querySelector('.cart-items__table-row[data-key]'));
}

{
  /**
   * THE TWO MOMENTS BEFORE CHECKOUT. A cart update is not the only way to
   * arrive at a wrong gift line — a cart built before this shipped has no book
   * in it and nothing will ever fire an update event over it. So the guard also
   * runs at the two places a buyer looks at the cart on the way out: the drawer
   * opening, and /cart loading.
   *
   * Both are gated on the page having rendered line items, so a visitor with an
   * empty cart never causes a request. That is why this is not simply run on
   * every page load: it would be one /cart.js per page view, for everyone, to
   * catch a state almost nobody is in.
   */
  const guardBeforeCheckout = () => {
    if (cartLooksNonEmpty()) reconcile();
  };

  document.addEventListener('theme-drawer:open', (event) => {
    const target = event.target;
    if (target instanceof Element && target.querySelector('cart-drawer-component')) guardBeforeCheckout();
  });

  const onCartPage = () => {
    let name;
    try {
      name = Theme?.template?.name;
    } catch {
      return;
    }
    if (name === 'cart') guardBeforeCheckout();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onCartPage, { once: true });
  } else {
    onCartPage();
  }
}
